import { FileImage, Paperclip, X } from "lucide-react";
import { useRef } from "react";

import { formatFileSize } from "../../shared/feedback-schemas";
import { Button } from "../components/ui/button";
import type { ComposerFile } from "./composer-files";

// ADR-0089: files on a reply or note. Each file uploads as soon as it is dropped, pasted, or
// picked, so sending only names files that are already stored.

function FileChip({ file, onRemove }: { file: ComposerFile; onRemove: () => void }) {
  const Icon = file.image ? FileImage : Paperclip;
  const detail =
    file.status === "uploading" ? "Uploading…" : (file.problem ?? formatFileSize(file.byteSize));
  return (
    <li
      className={
        file.status === "failed"
          ? "inline-flex items-center gap-1.5 rounded-full bg-destructive-background py-1 pr-1 pl-3 text-sm text-destructive"
          : "inline-flex items-center gap-1.5 rounded-full bg-muted py-1 pr-1 pl-3 text-sm"
      }
    >
      {file.preview ? (
        <img src={file.preview} alt="" className="size-6 rounded object-cover" />
      ) : (
        <Icon aria-hidden="true" className="size-3.5" />
      )}
      <span className="max-w-56 truncate">{file.filename}</span>
      <span className="text-muted-foreground">{detail}</span>
      <button
        type="button"
        aria-label={`Remove ${file.filename}`}
        className="rounded-full p-1 hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring"
        onClick={onRemove}
      >
        <X aria-hidden="true" className="size-3.5" />
      </button>
    </li>
  );
}

/** The files waiting to be sent, and the button that picks more. */
export function ComposerFiles({
  files,
  disabled,
  full,
  onAdd,
  onRemove,
}: {
  files: readonly ComposerFile[];
  disabled: boolean;
  full: boolean;
  onAdd: (files: readonly File[]) => void;
  onRemove: (key: string) => void;
}) {
  const picker = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col gap-2">
      {files.length > 0 ? (
        <ul aria-label="Files to send" className="flex flex-wrap gap-2">
          {files.map((file) => (
            <FileChip
              key={file.key}
              file={file}
              onRemove={() => {
                onRemove(file.key);
              }}
            />
          ))}
        </ul>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={picker}
          type="file"
          multiple
          hidden
          aria-label="Attach files"
          onChange={(event) => {
            onAdd([...(event.currentTarget.files ?? [])]);
            event.currentTarget.value = "";
          }}
        />
        <Button
          type="button"
          variant="outline"
          disabled={disabled || full}
          onClick={() => {
            picker.current?.click();
          }}
        >
          <Paperclip aria-hidden="true" />
          Attach files
        </Button>
        <span className="text-sm text-muted-foreground">
          {full
            ? "That is the most files one message can carry."
            : "Or drop files here, or paste an image."}
        </span>
      </div>
    </div>
  );
}
